"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { DROP_BATCH_STALE_TIME_MS, fetchDropsByIds, orderDropsByIds, seedDropCache } from "@/services/api/drop-api";
interface ArtistSubmission {
  id: string;
  imageUrl: string;
  mediaMimeType: string;
  title?: string | undefined;
  createdAt: number;
  drop?: ApiDrop | undefined;
}
interface User {
  id: string;
  handle?: string | null | undefined;
  level: number;
  cic: number;
  rep: number;
  pfp?: string | null | undefined;
  active_main_stage_submission_ids?: string[] | undefined;
}
interface UseUserArtSubmissionsReturn {
  submissions: ArtistSubmission[];
  submissionCount: number;
  isLoading: boolean;
  error: string | null;
}
interface UseSubmissionDropsReturn {
  submissionsWithDrops: ArtistSubmission[];
  isLoading: boolean;
  error: string | null;
}
const toArtistSubmission = (drop: ApiDrop): ArtistSubmission | null => {
  const firstMedia = drop.parts.at(0)?.media.at(0);
  if (!firstMedia?.url) {
    return null;
  }
  return {
    id: drop.id,
    imageUrl: firstMedia.url,
    mediaMimeType: firstMedia.mime_type,
    title: drop.title ?? undefined,
    createdAt: drop.created_at,
    drop
  };
};
export const useSubmissionDrops = submissions => {
  const $ = _c(28);
  const queryClient = useQueryClient();
  let map;
  if ($[0] !== submissions) {
    map = new Map();
    for (const submission of submissions) {
      if (submission.drop) {
        map.set(submission.id, submission.drop);
      }
    }
    $[0] = submissions;
    $[1] = map;
  } else {
    map = $[1];
  }
  const existingDropsById = map;
  let t0;
  if ($[2] !== existingDropsById || $[3] !== submissions) {
    let t1;
    if ($[5] !== existingDropsById) {
      t1 = submission_0 => !existingDropsById.has(submission_0.id);
      $[5] = existingDropsById;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    t0 = submissions.filter(t1).map(_temp);
    $[2] = existingDropsById;
    $[3] = submissions;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const missingDropIds = t0;
  let t1;
  if ($[7] !== missingDropIds) {
    t1 = missingDropIds.join(",");
    $[7] = missingDropIds;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const missingDropIdsKey = t1;
  let t2;
  if ($[9] !== missingDropIdsKey) {
    t2 = [QueryKey.DROP, {
      ids: missingDropIdsKey,
      scope: "submission-drops"
    }];
    $[9] = missingDropIdsKey;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== missingDropIds || $[12] !== queryClient) {
    t3 = async () => {
      const drops = await fetchDropsByIds(missingDropIds);
      seedDropCache(queryClient, drops);
      return drops;
    };
    $[11] = missingDropIds;
    $[12] = queryClient;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = missingDropIds.length > 0;
  let t5;
  if ($[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: DROP_BATCH_STALE_TIME_MS
    };
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const {
    data: t6,
    isLoading,
    error
  } = useQuery(t5);
  let t7;
  if ($[18] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const fetchedDrops = t7;
  let t8;
  if ($[20] !== existingDropsById || $[21] !== fetchedDrops || $[22] !== submissions) {
    const fetchedDropsById = new Map(fetchedDrops.map(_temp2));
    t8 = submissions.map(submission_2 => ({
      ...submission_2,
      drop: existingDropsById.get(submission_2.id) ?? fetchedDropsById.get(submission_2.id)
    })).filter(_temp3);
    $[20] = existingDropsById;
    $[21] = fetchedDrops;
    $[22] = submissions;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const submissionsWithDrops = t8;
  const t9 = error ? "Failed to load drop data" : null;
  let t10;
  if ($[24] !== isLoading || $[25] !== submissionsWithDrops || $[26] !== t9) {
    t10 = {
      submissionsWithDrops,
      isLoading,
      error: t9
    };
    $[24] = isLoading;
    $[25] = submissionsWithDrops;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
};
export const useUserArtSubmissions = user => {
  const $ = _c(25);
  const queryClient = useQueryClient();
  user?.handle;
  user?.id;
  const authorKey = user?.handle ?? user?.id;
  let t0;
  if ($[0] !== user?.active_main_stage_submission_ids) {
    t0 = () => user?.active_main_stage_submission_ids ?? [];
    $[0] = user?.active_main_stage_submission_ids;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  user?.active_main_stage_submission_ids;
  let t1;
  if ($[2] !== t0) {
    t1 = t0();
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const activeSubmissionIds = t1;
  let t2;
  if ($[4] !== activeSubmissionIds) {
    t2 = activeSubmissionIds.join(",");
    $[4] = activeSubmissionIds;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const activeSubmissionIdsKey = t2;
  let t3;
  if ($[6] !== activeSubmissionIdsKey) {
    t3 = {
      ids: activeSubmissionIdsKey
    };
    $[6] = activeSubmissionIdsKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== authorKey || $[9] !== t3) {
    t4 = [QueryKey.PROFILE_DROPS, authorKey, "art-submissions", t3];
    $[8] = authorKey;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const queryKey = t4 as const;
  let t5;
  if ($[11] !== activeSubmissionIds || $[12] !== queryClient) {
    t5 = async () => {
      const result = await fetchDropsByIds(activeSubmissionIds);
      seedDropCache(queryClient, result);
      return orderDropsByIds(activeSubmissionIds, result);
    };
    $[11] = activeSubmissionIds;
    $[12] = queryClient;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = !!user && activeSubmissionIds.length > 0;
  let t7;
  if ($[14] !== queryKey || $[15] !== t5 || $[16] !== t6) {
    t7 = {
      queryKey,
      queryFn: t5,
      enabled: t6,
      staleTime: DROP_BATCH_STALE_TIME_MS
    };
    $[14] = queryKey;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const {
    data: drops,
    isLoading,
    error
  } = useQuery(t7);
  let t8;
  bb0: {
    if (!drops || !user) {
      let t9;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [];
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      t8 = t9;
      break bb0;
    }
    let t9;
    if ($[19] !== drops) {
      t9 = drops.filter(_temp4).map(toArtistSubmission).filter(_temp5);
      $[19] = drops;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = t9;
  }
  const submissions = t8;
  const t9 = error?.message ?? null;
  let t10;
  if ($[21] !== isLoading || $[22] !== submissions || $[23] !== t9) {
    t10 = {
      submissions,
      submissionCount: submissions.length,
      isLoading,
      error: t9
    };
    $[21] = isLoading;
    $[22] = submissions;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};
function _temp(submission_1) {
  return submission_1.id;
}
function _temp2(drop) {
  return [drop.id, drop];
}
function _temp3(submission_3) {
  return submission_3.drop;
}
function _temp4(drop) {
  return drop.drop_type === ApiDropType.Participatory;
}
function _temp5(submission) {
  return !!submission;
}